import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function errMsg(err, fallback) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || String(d)).join(' ');
  return fallback;
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // OTP popup
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError('Enter your email address.');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/patient/forgot-password`, {
        email: email.trim(),
      });
      setInfo(res.data?.message || 'If an account exists for that email, a reset code has been sent.');
      setOtp('');
      setOtpError('');
      setOtpOpen(true);
    } catch (err) {
      setError(errMsg(err, 'Could not send a reset code. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    setOtpError('');
    if (!otp.trim()) {
      setOtpError('Enter the 6-digit code from your email.');
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/patient/verify-reset-otp`, {
        email: email.trim(),
        otp: otp.trim(),
      });
      const token = res.data?.reset_token;
      if (!token) {
        setOtpError('Something went wrong. Please request a new code.');
        return;
      }
      setOtpOpen(false);
      navigate(`/reset?token=${encodeURIComponent(token)}`);
    } catch (err) {
      setOtpError(errMsg(err, 'Invalid or expired code.'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #faf6f0 0%, #f5ebe0 100%)', p: 3,
    }}>
      <Card sx={{
        maxWidth: 460, width: '100%',
        background: 'linear-gradient(145deg, rgba(255,254,251,0.95) 0%, rgba(255,247,237,0.9) 100%)',
        border: '1px solid rgba(180, 83, 9, 0.15)',
        boxShadow: '0 25px 50px -12px rgba(180, 83, 9, 0.2)',
      }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
            <LockResetIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407',
          }}>
            Reset Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your email and we'll send a one-time code to reset your password.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}
          {info && <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>{info}</Alert>}

          <form onSubmit={sendCode}>
            <TextField fullWidth label="Email" type="email" InputLabelProps={{ shrink: true }} value={email}
              onChange={(e) => setEmail(e.target.value)} margin="normal" required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.8)' } }} />
            <Button fullWidth type="submit" variant="contained" size="large" disabled={sending}
              sx={{
                mt: 3, py: 1.5, background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
                fontWeight: 700, '&:hover': { background: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)' },
              }}>
              {sending ? 'Sending code...' : 'Send Reset Code'}
            </Button>
          </form>

          {info && (
            <Button sx={{ mt: 1 }} onClick={() => setOtpOpen(true)}>
              Already have a code? Enter it
            </Button>
          )}

          <Button sx={{ mt: 2 }} onClick={() => navigate('/login')}>
            Back to Sign in
          </Button>
        </CardContent>
      </Card>

      <Dialog open={otpOpen} onClose={() => setOtpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Enter reset code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
          </Typography>
          {otpError && <Alert severity="error" sx={{ mb: 2 }}>{otpError}</Alert>}
          <TextField
            fullWidth
            autoFocus
            label="6-digit code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.4rem' } }}
            onKeyDown={(e) => { if (e.key === 'Enter') verifyOtp(); }}
          />
          <Button size="small" sx={{ mt: 1 }} disabled={sending} onClick={() => sendCode()}>
            Resend code
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtpOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={verifyOtp} disabled={verifying}>
            {verifying ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
